import React, { useRef, useCallback, useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Bold, Italic, List, ListOrdered, Undo, Redo, Underline as UnderlineIcon } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const FONTS = [
  'IBM Plex Sans', 'Arial', 'Calibri', 'Times New Roman', 'Georgia', 'Verdana', 'Helvetica', 'Roboto', 'Open Sans'
];

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px'];

export const RichTextEditor = ({ value, onChange, placeholder, minHeight = '300px', language = 'pt' }) => {
  const editorRef = useRef(null);
  const [currentFont, setCurrentFont] = useState('IBM Plex Sans');
  const [currentSize, setCurrentSize] = useState('14px');
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      const sanitized = value ? DOMPurify.sanitize(value, { USE_PROFILES: { html: true } }) : '';
      if (editorRef.current.innerHTML !== sanitized) {
        editorRef.current.innerHTML = sanitized;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const execCmd = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleContentChange();
  }, []);

  const handleContentChange = useCallback(() => {
    if (editorRef.current && onChange) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleFontChange = useCallback((font) => {
    setCurrentFont(font);
    execCmd('fontName', font);
  }, [execCmd]);

  const handleSizeChange = useCallback((size) => {
    setCurrentSize(size);
    // fontSize command uses 1-7 scale, so we use a span approach
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && !sel.isCollapsed) {
      execCmd('fontSize', '7');
      // Replace the font size 7 with the actual size
      const fontElements = editorRef.current?.querySelectorAll('font[size="7"]');
      if (fontElements) {
        fontElements.forEach(el => {
          el.removeAttribute('size');
          el.style.fontSize = size;
        });
      }
      handleContentChange();
    }
  }, [execCmd, handleContentChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    if (html) {
      // Sanitize the pasted HTML (DOMPurify also strips scripts/styles/images/etc.)
      const clean = DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ['script', 'style', 'img', 'meta', 'link'],
      });
      document.execCommand('insertHTML', false, clean);
    } else {
      document.execCommand('insertText', false, text);
    }
    handleContentChange();
  }, [handleContentChange]);

  const toolbarBtn = (icon, cmd, title) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0 hover:bg-[#1E3C3C]/10 text-[#374151]"
      onClick={() => execCmd(cmd)}
      title={title}
      data-testid={`rte-${cmd}-btn`}
    >
      {icon}
    </Button>
  );

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b bg-[#f8fafc]">
        <Select value={currentFont} onValueChange={handleFontChange}>
          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="rte-font-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONTS.map(f => (
              <SelectItem key={f} value={f} style={{ fontFamily: f }}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={currentSize} onValueChange={handleSizeChange}>
          <SelectTrigger className="h-8 w-[70px] text-xs" data-testid="rte-size-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZES.map(s => (
              <SelectItem key={s} value={s}>{s.replace('px', '')}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="w-px h-6 bg-gray-200 mx-1" />

        {toolbarBtn(<Bold className="w-4 h-4" />, 'bold', 'Bold (Ctrl+B)')}
        {toolbarBtn(<Italic className="w-4 h-4" />, 'italic', 'Italic (Ctrl+I)')}
        {toolbarBtn(<UnderlineIcon className="w-4 h-4" />, 'underline', 'Underline (Ctrl+U)')}

        <div className="w-px h-6 bg-gray-200 mx-1" />

        {toolbarBtn(<List className="w-4 h-4" />, 'insertUnorderedList', language === 'pt' ? 'Lista com marcadores' : 'Bullet list')}
        {toolbarBtn(<ListOrdered className="w-4 h-4" />, 'insertOrderedList', language === 'pt' ? 'Lista numerada' : 'Numbered list')}

        <div className="w-px h-6 bg-gray-200 mx-1" />

        {toolbarBtn(<Undo className="w-4 h-4" />, 'undo', 'Undo')}
        {toolbarBtn(<Redo className="w-4 h-4" />, 'redo', 'Redo')}
      </div>

      {/* Editor Area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleContentChange}
        onPaste={handlePaste}
        className="px-4 py-3 outline-none overflow-y-auto text-sm text-[#1a202c] prose prose-sm max-w-none"
        style={{
          minHeight,
          maxHeight: '500px',
          fontFamily: currentFont,
          fontSize: currentSize,
        }}
        data-placeholder={placeholder || (language === 'pt' ? 'Cole ou escreva aqui o conteúdo do CV...' : 'Paste or write CV content here...')}
        data-testid="rte-content-area"
      />
    </div>
  );
};
